import { ICustomer } from "shared-types/index";
import PhoneNumberService from 'external-services/phone-number-service/index';
import {BookingType, CustomerTypes, SEARCH_FIELDS} from "app/app.types";
import moment from "moment";
import {
  PHONE_VALIDATION_MESSAGE,
  PAYMENT_VALIDATION_MESSAGE,
  RECURRING_BOOKING_VALIDATION_MESSAGE,
  BOOKING_VALIDATION_MESSAGE,
  SBL_EMAIL_VALIDATION_MESSAGE,
  SBL_SMSVALIDATION_MESSAGE,
  EMAIL_INVALID_MESSAGE,
  EMAIL_REQUIRED_MESSAGE,
  EMAIL_TOO_LONG_MESSAGE,
  PHONE_ERROR_MESSAGE, PRIVATE_FUN_BOOKING_VALIDATION_MESSAGE,
} from './customerValidation.types';
import BookingValidationService from 'shared-services/booking-validation-service';
const NS = 'CustomerValidationService'; // jshint ignore:line

class CustomerValidationService {

  // private PHONE_VALIDATION_MESSAGE_ON_NON_PHONE_FIELD: string = 'You are searching on phone number, please enter 6 or more digits';

  public emailValidation(customer: ICustomer, isNoPayment?: boolean, isRecurringBooking?: boolean, isEditCustomer?: boolean,
                         sblActive?: boolean, sblEmail?: boolean, isBookedBy?: boolean, isPrivateFunEvent?: boolean): {
    valid: boolean;
    msg?: string;
  } {
    if (customer.email) {
      const emailValid = this.emailValidationCheck(customer.email);
      return {valid: emailValid.isEmailValid, msg: emailValid.emailErrorMsg}
    }
    if (sblActive && sblEmail && !customer.email) {
      return {
        valid: false,
        msg: SBL_EMAIL_VALIDATION_MESSAGE
      };
    }
    if (isPrivateFunEvent && (!customer.email && !customer.phone) && isNoPayment) {
      return {
        valid: false,
        msg: PRIVATE_FUN_BOOKING_VALIDATION_MESSAGE
      };
    }
    if (isEditCustomer && (!customer.email && !customer.phone)) {
      return {
        valid: false,
        msg: BOOKING_VALIDATION_MESSAGE
      };
    } else if (!sblActive && !isNoPayment && (!customer.phone && !customer.email) && !isBookedBy) {
      return {
        valid: false,
        msg: PAYMENT_VALIDATION_MESSAGE
      };
    } else if (!sblActive && isRecurringBooking && (!customer.phone && !customer.email)) {
      return {
        valid: false,
        msg: RECURRING_BOOKING_VALIDATION_MESSAGE
      };
    }
    return {
      valid: true
    };
  }
  public getCustomerValue(customer: ICustomer, ignoreCompany = false) {
    const searchFields = customer?.customerType === 'inline'? [SEARCH_FIELDS.EMAIL, SEARCH_FIELDS.PHONE, SEARCH_FIELDS.FIRST_NAME] : Object.values(SEARCH_FIELDS);
    return Object.keys(customer)
      .filter((key) => searchFields.includes(key as SEARCH_FIELDS)
        && customer[key as keyof ICustomer]
        && (key !== SEARCH_FIELDS.FIRST_NAME || customer[key]?.toLowerCase() !== 'walk in')
        // for new customer form search, company is not required
        && (key !== SEARCH_FIELDS.COMPANY || !ignoreCompany)
      ).map(key => {
        return {
          fieldName: key,
          value: customer[key as keyof ICustomer]
        };
      });
  }

  public emailValidationLink(customerEmail: string): {
    isEmailValid: boolean;
    emailErrorMsg?: string;
  } {
    return this.emailValidationCheck(customerEmail);
  }

  public emailValidationCheck(email: string, isEmailRequired = true): {
    isEmailValid: boolean;
    emailErrorMsg?: string;
  } {

    const valid = BookingValidationService.validateEmail(email);

    if (!valid) {
      return {
        isEmailValid: false,
        emailErrorMsg: EMAIL_INVALID_MESSAGE,
      };
    } else if (isEmailRequired && email === '') {
      return {
        isEmailValid: false,
        emailErrorMsg: EMAIL_REQUIRED_MESSAGE,
      };
    }
    if (email) {
      const emailParts = email.split('@');
      if (emailParts[0].indexOf('.') === 0 || emailParts[0].indexOf('.') === emailParts[0].length - 1
        || emailParts[1].indexOf('-') === 0 || emailParts[1].indexOf('-') === emailParts[1].length - 1
      ) {
        return {
          isEmailValid: false,
          emailErrorMsg: EMAIL_INVALID_MESSAGE,
        };
      }
      if (email.length > 100 || emailParts[1].length > 63) {
        return {
          isEmailValid: false,
          emailErrorMsg: EMAIL_TOO_LONG_MESSAGE,
        };
      }
    }
    return {
      isEmailValid: true
    };
  }

  public validateCustomer(
    customer: ICustomer,
    originCustomer: ICustomer,
    emailRequired: boolean,
    ignorePhoneValidation: boolean,
    surNameRequired: boolean = false
  ): {
    isEdited: boolean,
    isValid: boolean,
    isBirthdayValid: boolean,
  } {
    let isValid = false;
    let isEdited = true;
    let isBirthdayValid = null;
    if(originCustomer) isEdited = this.hasCustomerChanged(originCustomer, customer);

    // First name and last name is requried due to NBI-6196
    const hasName = customer && (surNameRequired ? (customer.lastName && customer.firstName) : (customer.lastName || customer.firstName))
    const isInline = customer && customer.customerType === CustomerTypes.inline; // used for standbyList and waitList

    if (hasName) {
      const birthdayValid = this.isValidBirthday(customer);
      if (isInline) {
        // no phone & no email needed
        if (!emailRequired && ignorePhoneValidation) {
          isValid = true;
        }

        // just email needed
        if (emailRequired && ignorePhoneValidation && customer.email) {
          isValid = true;
        }

        // just phone needed for sbl
        if (!ignorePhoneValidation && customer.phone) {
          isValid = true;
        }

        // both phone and email needed
        if (emailRequired && !ignorePhoneValidation && customer.email && customer.phone) {
          isValid = true;
        }
        return {
          isValid,
          isEdited,
          isBirthdayValid,
        };
        // full customer details version
      } else if (birthdayValid) {
        isValid = true;
      }
      isBirthdayValid = birthdayValid;
    }
    return {
      isValid,
      isEdited,
      isBirthdayValid,
    };
  }

  public getValidationError(phoneNumberTooShort: boolean, phone: string, email: string,
                                 isPhoneTouched: boolean, isPhoneValid: boolean, displayErrors: any, isNoPayment?: boolean, isRecurringBooking?: boolean,
                                 isEditCustomer?: boolean, isPhoneDirty?: boolean, sblActive?: boolean, sblSms?: boolean, isBookedBy?: boolean,  isPrivateFunEvent?: boolean) {
    if (phoneNumberTooShort && (phone && isPhoneTouched))
      return PHONE_VALIDATION_MESSAGE;
    if (!phoneNumberTooShort && (phone && !isPhoneValid))
      return PHONE_ERROR_MESSAGE;
    if (!phoneNumberTooShort && (isPhoneDirty || displayErrors) && sblActive && sblSms && !phone)
      return SBL_SMSVALIDATION_MESSAGE;
    if (!phoneNumberTooShort && isEditCustomer && (!email && !phone))
      return BOOKING_VALIDATION_MESSAGE;
    if (!isEditCustomer && !sblActive) {
      if (!phoneNumberTooShort
        && (isPhoneDirty || displayErrors)
        && (!email && !phone)) {

        if (!isBookedBy) {

          if (isPrivateFunEvent && isNoPayment) {
            return PRIVATE_FUN_BOOKING_VALIDATION_MESSAGE;
          }

          if (!isNoPayment) {
            return PAYMENT_VALIDATION_MESSAGE;
          }
        }

        if (isRecurringBooking) {
          return RECURRING_BOOKING_VALIDATION_MESSAGE;
        }
      }
    }
    return '';
  }

  public checkIsPhoneNumberType(phone: string): boolean {
    if(phone) {
      const parseNumber = PhoneNumberService.parseNumber(phone, true);
      return parseNumber && typeof parseNumber !== 'string';
    }else{
      return true;
    }
  }

  public checkBookedByValid (bookedByErrors: string, isBookedBy: boolean, bookedByCustomerDetails: ICustomer, isSurnameRequired: boolean = false) {
    let isBookedByValid = true;
    if (bookedByErrors && isBookedBy) {
      isBookedByValid = (Object.values(bookedByErrors).filter(i => !!i).length === 0)
      // First name and last name is requried due to NBI-6196
        && (isSurnameRequired ? (!!bookedByCustomerDetails.firstName && !!bookedByCustomerDetails.lastName) : (!!bookedByCustomerDetails.firstName || !!bookedByCustomerDetails.lastName)) && (!!bookedByCustomerDetails.phone || !!bookedByCustomerDetails.email);
    }
    return isBookedByValid;
  }

  private hasCustomerChanged(originCustomer: ICustomer, currentCustomer: ICustomer): boolean {
    const originPhone = PhoneNumberService.stripPhoneFormatting(originCustomer.phone);
    const currentPhone = PhoneNumberService.stripPhoneFormatting(currentCustomer.phone);
    if(originCustomer.firstName != currentCustomer.firstName) return true;
    else if(originCustomer.lastName != currentCustomer.lastName) return true;
    else if(originPhone != currentPhone) return true;
    else if(originCustomer.email != currentCustomer.email) return true;
    else return false;
  }

  private isValidBirthday(customer: ICustomer): boolean {
    let birthdayValid = false;
    // if day, month and year present validate as normal
    if (customer.birthdayYear && customer.birthdayMonth && customer.birthdayDate) {
      birthdayValid = moment(customer.birthdayYear + '-' + customer.birthdayMonth + '-' + customer.birthdayDate, 'YYYY-MM-DD').isValid();
    }
    // if only day and month present, validate assuming it's a leap year
    else if (customer.birthdayMonth && customer.birthdayDate) {
      birthdayValid = moment('1980-' + customer.birthdayMonth + '-' + customer.birthdayDate, 'YYYY-MM-DD').isValid();
    }
    // in other cases don't validate
    else {
      birthdayValid = true;
    }

    return birthdayValid;
  }
}

const instance = new CustomerValidationService();
export default instance as CustomerValidationService;
